// =============================================================================
// SEVRIC Chat — React hooks
// -----------------------------------------------------------------------------
// One listener per resource shared across the page. Every hook returns
// `{ data, loading, error }` so call sites have a consistent shape.
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { getSupabase } from "./client.js";

// =============================================================================
// useThreadList — combined sidebar feed (Teams + DMs + Unit threads).
// Returns separated arrays so each section can render independently.
// Subscribes to changes so newly-active threads bubble up live.
// =============================================================================
export function useThreadList({ uid, tenantId, includeArchived = false }) {
  const [channels, setChannels] = useState([]);
  const [dms, setDms] = useState([]);
  const [entityThreads, setEntityThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uid || !tenantId) return;
    const supa = getSupabase();
    let cancelled = false;

    async function load() {
      try {
        const [chRes, dmRes, etRes] = await Promise.all([
          // Channels the caller belongs to. The join through channel_members
          // is enforced by RLS — private channels you're not a member of
          // are filtered server-side.
          supa
            .from("channels")
            .select("id,name,slug,icon,accent,is_private,last_message_at")
            .order("last_message_at", { ascending: false, nullsFirst: false }),
          // DMs with a join through sevric_users for BOTH participants so
          // we render the actual person's display name + initials in the
          // sidebar — never the raw uid.
          supa
            .from("dms")
            .select("id,participant_a,participant_b,is_ai,last_message_at,a:sevric_users!dms_participant_a_fkey(uid,display_name,initials,presence),b:sevric_users!dms_participant_b_fkey(uid,display_name,initials,presence)")
            .order("last_message_at", { ascending: false, nullsFirst: false }),
          supa
            .from("entity_threads")
            .select("id,entity_kind,entity_id,entity_label,status,last_message_at,archived_at")
            .in("status", includeArchived ? ["active", "archived"] : ["active"])
            .order("last_message_at", { ascending: false, nullsFirst: false })
            .limit(40),
        ]);
        if (cancelled) return;
        if (chRes.error) throw chRes.error;
        // DM join above uses an explicit foreign-key relationship name; if
        // Supabase can't auto-detect the FK, the simpler select below is the
        // fallback — we'll just fetch sevric_users separately. Try this once
        // before giving up so a missing FK isn't fatal.
        let dmRows = dmRes.data;
        if (dmRes.error) {
          const fallback = await supa
            .from("dms")
            .select("id,participant_a,participant_b,is_ai,last_message_at")
            .order("last_message_at", { ascending: false, nullsFirst: false });
          if (fallback.error) throw fallback.error;
          // Enrich participants by querying sevric_users in one round trip.
          const uids = [...new Set((fallback.data || []).flatMap((d) => [d.participant_a, d.participant_b]))];
          const users = uids.length
            ? await supa.from("sevric_users").select("uid,display_name,initials,presence").in("uid", uids)
            : { data: [] };
          const map = new Map((users.data || []).map((u) => [u.uid, u]));
          dmRows = (fallback.data || []).map((d) => ({
            ...d,
            a: map.get(d.participant_a) || { uid: d.participant_a },
            b: map.get(d.participant_b) || { uid: d.participant_b },
          }));
        }
        if (etRes.error) throw etRes.error;
        setChannels(chRes.data || []);
        setDms(dmRows || []);
        setEntityThreads(etRes.data || []);
        setLoading(false);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setLoading(false);
        }
      }
    }
    load();

    // Live: any new message bumps last_message_at on the parent scope, which
    // we subscribe to here. Cheaper than subscribing to the messages table.
    const ch = supa
      .channel(`thread-list:${uid}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "channels" }, load)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "dms" }, load)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "entity_threads" }, load)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "channels" }, load)
      .subscribe();

    return () => { cancelled = true; supa.removeChannel(ch); };
  }, [uid, tenantId, includeArchived]);

  return { channels, dms, entityThreads, loading, error };
}

// =============================================================================
// useThread — messages + presence for a single scope.
// scope = { kind: 'channel'|'dm'|'entity_thread', id: uuid }
// =============================================================================
export function useThread({ scope, pageSize = 50 }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!scope?.id) return;
    const supa = getSupabase();
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const res = await supa
          .from("messages")
          .select("id,scope_kind,scope_id,author_uid,author_display_name,author_initials,body,message_kind,attachments,reply_to,mentions,ai_meta,edited_at,created_at")
          .eq("scope_kind", scope.kind)
          .eq("scope_id", scope.id)
          .is("deleted_at", null)
          .order("created_at", { ascending: false })
          .limit(pageSize);
        if (cancelled) return;
        if (res.error) throw res.error;
        const rows = (res.data || []).reverse();
        setMessages(rows);
        setHasMore((res.data || []).length === pageSize);
        setLoading(false);
        setError(null);
      } catch (err) {
        if (!cancelled) { setError(err); setLoading(false); }
      }
    }
    load();

    // Realtime: new messages append; updates replace; soft-deletes filter.
    const ch = supa
      .channel(`thread:${scope.kind}:${scope.id}`)
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `scope_id=eq.${scope.id}` },
        (payload) => setMessages((prev) => [...prev, payload.new])
      )
      .on("postgres_changes",
        { event: "UPDATE", schema: "public", table: "messages", filter: `scope_id=eq.${scope.id}` },
        (payload) => setMessages((prev) => prev.map((m) => m.id === payload.new.id ? payload.new : m).filter((m) => !m.deleted_at))
      )
      .subscribe();

    return () => { cancelled = true; supa.removeChannel(ch); };
  }, [scope?.kind, scope?.id, pageSize]);

  return { messages, loading, error, hasMore };
}

// =============================================================================
// useMembers — who's in this channel / DM. Includes presence (online/busy/etc).
// =============================================================================
export function useMembers({ scope, tenantId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!scope?.id || !tenantId) return;
    const supa = getSupabase();
    let cancelled = false;

    async function load() {
      try {
        if (scope.kind === "channel") {
          const res = await supa
            .from("channel_members")
            .select("uid, role, sevric_users!inner(uid, display_name, initials, avatar_color, presence, presence_label)")
            .eq("channel_id", scope.id);
          if (cancelled) return;
          setMembers((res.data || []).map((r) => ({ ...r.sevric_users, role: r.role })));
        } else if (scope.kind === "dm") {
          const dm = await supa.from("dms")
            .select("participant_a,participant_b")
            .eq("id", scope.id).single();
          const uids = [dm.data?.participant_a, dm.data?.participant_b].filter(Boolean);
          const res = await supa.from("sevric_users")
            .select("uid, display_name, initials, avatar_color, presence, presence_label")
            .in("uid", uids);
          if (cancelled) return;
          setMembers(res.data || []);
        } else {
          // Entity threads — anyone who has posted in the thread is implicitly a member
          const res = await supa.from("messages")
            .select("author_uid")
            .eq("scope_kind", "entity_thread")
            .eq("scope_id", scope.id);
          const authorUids = [...new Set((res.data || []).map((m) => m.author_uid))];
          if (authorUids.length === 0) { setMembers([]); setLoading(false); return; }
          const us = await supa.from("sevric_users")
            .select("uid, display_name, initials, avatar_color, presence, presence_label")
            .in("uid", authorUids);
          if (cancelled) return;
          setMembers(us.data || []);
        }
        setLoading(false);
      } catch {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [scope?.kind, scope?.id, tenantId]);

  return { members, loading };
}

// =============================================================================
// useScopeMeta — load the channel row OR entity_thread row OR DM row for the
// thread header. Returns enough to render the entity strip, channel name +
// member count, or DM other-party name.
// =============================================================================
export function useScopeMeta({ scope, callerUid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!scope?.id) { setData(null); setLoading(false); return; }
    const supa = getSupabase();
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        if (scope.kind === "channel") {
          const [ch, count] = await Promise.all([
            supa.from("channels").select("id,name,icon,accent,description,is_private").eq("id", scope.id).maybeSingle(),
            supa.from("channel_members").select("uid", { count: "exact", head: true }).eq("channel_id", scope.id),
          ]);
          if (cancelled) return;
          setData({ ...(ch.data || {}), memberCount: count.count || 0 });
        } else if (scope.kind === "entity_thread") {
          const et = await supa.from("entity_threads").select("*").eq("id", scope.id).maybeSingle();
          if (cancelled) return;
          setData(et.data || null);
        } else if (scope.kind === "dm") {
          const dm = await supa.from("dms").select("*").eq("id", scope.id).maybeSingle();
          if (cancelled) return;
          if (!dm.data) { setData(null); setLoading(false); return; }
          const otherUid = dm.data.participant_a === callerUid ? dm.data.participant_b : dm.data.participant_a;
          if (otherUid === "sevric-ai") {
            setData({ kind: "ai", otherUid, displayName: "SEVRIC AI", initials: "AI" });
          } else {
            const u = await supa.from("sevric_users").select("uid,display_name,initials,presence,presence_label").eq("uid", otherUid).maybeSingle();
            setData({
              kind: "human",
              otherUid,
              displayName: u.data?.display_name || otherUid,
              initials: u.data?.initials || "??",
              presence: u.data?.presence || "offline",
              presenceLabel: u.data?.presence_label || "",
            });
          }
        }
        setLoading(false);
      } catch {
        if (!cancelled) { setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [scope?.kind, scope?.id, callerUid]);

  return { data, loading };
}

// =============================================================================
// useArchivedThreads — the "Past units" view. Searchable by entity_label,
// loaded on demand from the Archive modal.
// =============================================================================
export function useArchivedThreads({ tenantId, search = "", limit = 50 }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    const supa = getSupabase();
    let cancelled = false;

    async function load() {
      setLoading(true);
      let q = supa
        .from("entity_threads")
        .select("id,entity_kind,entity_id,entity_label,archived_at,archived_reason,last_message_at")
        .eq("status", "archived")
        .order("archived_at", { ascending: false })
        .limit(limit);
      if (search) q = q.ilike("entity_label", `%${search}%`);
      const res = await q;
      if (cancelled) return;
      setData(res.data || []);
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [tenantId, search, limit]);

  return { data, loading };
}

// =============================================================================
// useUnreadMap — returns { [scopeKey]: boolean } where scopeKey is
// `${scope_kind}:${scope_id}` and the value is true if last_message_at >
// last_read_at. v1 shows a dot, not a count — count comes in v2 when we
// add a denormalized unread_count column.
// =============================================================================
export function useUnreadMap({ uid, channels, dms, entityThreads }) {
  const [map, setMap] = useState({});

  useEffect(() => {
    if (!uid) return;
    const supa = getSupabase();
    let cancelled = false;
    (async () => {
      try {
        const res = await supa.from("read_receipts")
          .select("scope_kind,scope_id,last_read_at").eq("uid", uid);
        if (cancelled) return;
        const readMap = new Map();
        for (const r of (res.data || [])) {
          readMap.set(`${r.scope_kind}:${r.scope_id}`, r.last_read_at);
        }
        const out = {};
        const consider = (kind, rows) => {
          for (const row of rows || []) {
            const key = `${kind}:${row.id}`;
            const lastMsg = row.last_message_at;
            if (!lastMsg) { out[key] = false; continue; }
            const lastRead = readMap.get(key);
            out[key] = !lastRead || new Date(lastMsg) > new Date(lastRead);
          }
        };
        consider("channel", channels);
        consider("dm", dms);
        consider("entity_thread", entityThreads);
        setMap(out);
      } catch { /* swallow — unread dots are not critical UX */ }
    })();
  }, [uid, channels?.length, dms?.length, entityThreads?.length,
      channels?.map?.((c) => c.last_message_at)?.join("|"),
      dms?.map?.((d) => d.last_message_at)?.join("|"),
      entityThreads?.map?.((e) => e.last_message_at)?.join("|")]);

  return map;
}

// =============================================================================
// Author rendering helpers — render the snapshot's display name if present,
// otherwise fall back to initials, otherwise "Someone". Mirrors the helper
// pattern from useBatteryData.js but for chat author rows.
// =============================================================================
export function authorName(author) {
  if (!author) return "Someone";
  return author.author_display_name || author.author_initials || "Someone";
}
export function authorInitials(author) {
  if (!author) return "??";
  if (author.author_initials) return author.author_initials.toUpperCase();
  const dn = author.author_display_name || "";
  const parts = dn.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || "?").slice(0, 2).toUpperCase();
}
export function isSevricAI(author) {
  return author?.author_uid === "sevric-ai";
}
